import type {
  PublicVisitorAvailability
} from '../../client'
import {
  AvailabilityDot
} from './AvailabilityDot'
import type {
  AvailabilityProvider
} from './visitorCalendarProviders'
import {
  availableProviders
} from './visitorCalendarProviders'
import type {
  VisitorAvailabilityState
} from './useVisitorAvailability'
import {
  GOOGLE_ICON,
  MICROSOFT_ICON
} from '../ProviderIcons'

const PROVIDER_ICONS: Record<AvailabilityProvider, JSX.Element> = {
  google: GOOGLE_ICON,
  microsoft: MICROSOFT_ICON
}

// The connect / legend / toggle strip above the time list for the visitor-
// availability overlay ("show times you're free"). Pure presentation — OAuth,
// analytics and the lead POST live in useVisitorAvailability.
export function VisitorAvailabilityControls({
  config,
  state,
  hideBusy,
  onConnect,
  onDisconnect,
  onToggleHideBusy
}: {
  config: PublicVisitorAvailability,
  state: VisitorAvailabilityState,
  hideBusy: boolean,
  onConnect: (provider: AvailabilityProvider) => void,
  onDisconnect: () => void,
  onToggleHideBusy: (hide: boolean) => void
}): JSX.Element | null {
  if (state.status === 'connected') {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5">
            <AvailabilityDot status="free" />
            <span>free</span>
          </span>
          <span className="flex items-center gap-1.5">
            <AvailabilityDot status="busy" />
            <span>busy</span>
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideBusy}
              onChange={(e) => onToggleHideBusy(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span>Hide busy times</span>
          </label>
        </div>
        <div className="mt-1 text-gray-400 break-all">
          {state.email}
          {' · '}
          <button
            type="button"
            onClick={onDisconnect}
            className="text-primary-500 hover:underline"
          >
            Disconnect
          </button>
        </div>
      </div>
    )
  }

  if (state.status === 'connecting') {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500">
        Checking your calendar…
      </div>
    )
  }

  const providers = availableProviders(config)
  if (providers.length === 0) return null

  return (
    <div className="mb-4 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-600">
          {state.status === 'error'
            ? 'Couldn’t read your calendar — try again, or just pick a time.'
            : 'See which times work for you.'}
        </p>
        <div className="flex gap-2">
          {providers.map((provider) => (
            <button
              key={provider.id}
              type="button"
              onClick={() => onConnect(provider.id)}
              aria-label={provider.label}
              title={provider.label}
              className="p-2.5 bg-white border border-gray-300 rounded-md hover:border-primary-500 transition-colors"
            >
              {PROVIDER_ICONS[provider.id]}
            </button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">
        Read-only, checked in your browser; we keep just your name and email —{' '}
        <a
          href="/privacy"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-gray-500"
        >
          privacy
        </a>.
      </p>
    </div>
  )
}
